import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { FlaskIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

function fmtErr(detail) {
  if (detail == null) return "Login failed. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={loc.state?.from || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Signed in");
      nav(loc.state?.from || "/", { replace: true });
    } catch (e) {
      setErr(fmtErr(e?.response?.data?.detail) || e.message || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-[calc(100vh-64px)] grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <section className="hidden lg:flex flex-col justify-between p-12 bg-[var(--surface)] border-r border-[var(--border)]">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-[var(--primary)] flex items-center justify-center">
            <FlaskIcon size={20} weight="bold" color="#fff" />
          </div>
          <div>
            <div className="font-heading text-base font-semibold tracking-tight">EU Glossary</div>
            <div className="label-kicker mt-0.5">Reg. 2025/1175</div>
          </div>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-medium tracking-tighter leading-tight">
            Cosmetic ingredient compliance,<br />precisely catalogued.
          </h1>
          <p className="mt-4 text-sm text-[var(--text-muted)] max-w-md leading-relaxed">
            Sign in to access the ingredient directory, build formulations, and run live
            compliance checks against EU Reg. 2025/1175.
          </p>
        </div>
        <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-widest">
          ROLES · ADMIN &nbsp;·&nbsp; EDITOR &nbsp;·&nbsp; VIEWER
        </div>
      </section>

      <section className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="label-kicker">Sign In</div>
          <h2 className="font-heading text-3xl font-medium tracking-tight mt-2">Welcome back.</h2>
          <p className="mt-2 text-sm text-[var(--text-muted)]">Enter your credentials to continue.</p>

          <div className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="label-kicker">Email</Label>
              <Input
                id="email" type="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="rounded-none h-10 font-mono text-sm mt-2"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-kicker">Password</Label>
              <Input
                id="password" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-none h-10 font-mono text-sm mt-2"
                data-testid="login-password"
              />
            </div>
            {err && (
              <div className="border border-[var(--danger)] bg-[var(--danger)]/5 px-3 py-2 font-mono text-[11px] text-[var(--danger)]" data-testid="login-error">
                {err}
              </div>
            )}
            <Button type="submit" disabled={busy} className="btn-primary rounded-none h-10 w-full font-mono text-[11px] uppercase tracking-[0.18em]" data-testid="login-submit">
              {busy ? "Signing in…" : (<>Sign In <ArrowRightIcon size={14} className="ml-2" weight="bold" /></>)}
            </Button>
          </div>
        </form>
      </section>
    </main>
  );
}
